import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, FileText, Presentation } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import {
  ODOO_ONE_PAGER_URL,
  integrationSteps,
  partnershipHighlights,
  partnershipOpportunities,
  partnershipTags,
} from '../content/odooPartnership';

const OdooPartnershipHome = () => {
  return (
    <div className="min-h-screen bg-[#FAFAF8] text-gray-900">
      <Header />

      <main className="pt-20 pb-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-center gap-2 mb-6">
            <span className="text-sm font-medium px-3 py-1 rounded-full bg-[#EAF3DE] text-[#3B6D11] border border-[#C0DD97]">
              Kolanut.ai
            </span>
            <span className="text-gray-400 text-sm">×</span>
            <span className="text-sm font-medium px-3 py-1 rounded-full bg-[#F1EFE8] text-gray-700 border border-[#D3D1C7]">
              Odoo
            </span>
          </div>

          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight leading-[1.12] max-w-2xl">
            Building the{' '}
            <span className="text-[#3B6D11]">AI growth</span> operating system for SMBs
          </h1>
          <p className="mt-4 text-lg text-gray-600 leading-relaxed max-w-2xl">
            Odoo manages operations. Kolanut drives growth. Together you get one system from first lead to repeat
            revenue.
          </p>

          <div className="mt-8 flex flex-col sm:flex-row flex-wrap gap-3">
            <Link
              to="/odoo/one-pager"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-[#3B6D11] text-white font-semibold px-6 py-3 text-sm hover:bg-[#27500A] transition-colors"
            >
              <FileText className="h-4 w-4" aria-hidden />
              Read partnership summary
            </Link>
            <Link
              to="/odoo/slide-deck"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-white text-[#3B6D11] font-semibold px-6 py-3 text-sm border border-[#97C459] hover:bg-[#F4FAE9] transition-colors"
            >
              <Presentation className="h-4 w-4" aria-hidden />
              Slide deck
            </Link>
            <a
              href={ODOO_ONE_PAGER_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-1 text-sm font-medium text-gray-600 hover:text-[#3B6D11] px-2"
            >
              Open summary in new tab
              <ArrowRight className="h-4 w-4" />
            </a>
          </div>
        </div>

        <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 mt-14">
          <p className="text-xs font-semibold uppercase tracking-[0.14em] text-[#3B6D11] mb-4">The opportunity</p>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="rounded-2xl border border-[#C0DD97] bg-[#F4FAE9] p-5">
              <h2 className="font-semibold text-gray-900 mb-2">Odoo</h2>
              <p className="text-sm text-gray-600 leading-relaxed">
                CRM, ERP, accounting, inventory, eCommerce, and workflow automation. The operational backbone.
              </p>
            </div>
            <div className="rounded-2xl border border-gray-200 bg-white p-5">
              <h2 className="font-semibold text-gray-900 mb-2">Kolanut.ai</h2>
              <p className="text-sm text-gray-600 leading-relaxed">
                Ad waste detection, acquisition optimization, and revenue attribution. The growth intelligence layer.
              </p>
            </div>
          </div>

          <div className="mt-6 flex flex-col sm:flex-row items-stretch gap-2 sm:gap-0 sm:items-center text-center text-sm">
            <div className="flex-1 rounded-xl bg-[#EAF3DE] border border-[#97C459] px-4 py-3 font-medium text-[#27500A]">
              Acquire customers
              <span className="block text-xs font-normal text-[#3B6D11] mt-0.5">via Kolanut.ai</span>
            </div>
            <span className="hidden sm:block text-[#97C459] px-2">→</span>
            <div className="flex-1 rounded-xl bg-white border border-gray-200 px-4 py-3 text-gray-900">
              Convert and manage
              <span className="block text-xs text-gray-500 mt-0.5">inside Odoo CRM</span>
            </div>
            <span className="hidden sm:block text-[#97C459] px-2">→</span>
            <div className="flex-1 rounded-xl bg-white border border-gray-200 px-4 py-3 text-gray-900">
              Improve LTV and retention
            </div>
          </div>
        </section>

        <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 mt-14">
          <p className="text-xs font-semibold uppercase tracking-[0.14em] text-[#3B6D11] mb-4">Why now</p>
          <p className="text-base text-gray-700 leading-relaxed mb-5">
            Customer acquisition costs on Meta and Google keep rising, yet most SMBs have no clear view of what is
            actually generating revenue. They need fewer tools, better attribution, and practical AI help without
            enterprise bloat.
          </p>
          <ul className="space-y-3">
            {partnershipHighlights.map((item) => (
              <li key={item} className="flex items-start gap-3 text-sm text-gray-800">
                <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-[#639922]" aria-hidden />
                {item}
              </li>
            ))}
          </ul>
        </section>

        <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 mt-14">
          <p className="text-xs font-semibold uppercase tracking-[0.14em] text-[#3B6D11] mb-4">How the integration works</p>
          <ol className="space-y-4">
            {integrationSteps.map((step, i) => (
              <li key={step} className="flex items-start gap-3">
                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#639922] text-white text-xs font-semibold">
                  {i + 1}
                </span>
                <p className="text-sm text-gray-800 leading-relaxed pt-1">{step}</p>
              </li>
            ))}
          </ol>
        </section>

        <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 mt-14">
          <p className="text-xs font-semibold uppercase tracking-[0.14em] text-[#3B6D11] mb-4">Partnership opportunities</p>
          <div className="grid sm:grid-cols-2 gap-4">
            {partnershipOpportunities.map((item) => (
              <div key={item.title} className="rounded-2xl border border-gray-200 bg-white p-5">
                <h3 className="font-semibold text-gray-900 mb-1.5">{item.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{item.body}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 mt-14">
          <div className="rounded-2xl border border-[#97C459] bg-[#EAF3DE] p-6 sm:p-8">
            <h2 className="text-xl sm:text-2xl font-bold text-[#27500A] leading-snug mb-3">
              The AI growth OS for the next generation of SMBs
            </h2>
            <p className="text-sm text-[#3B6D11] leading-relaxed">
              Affordable and built for SMBs. Kolanut extends Odoo&apos;s operational strength with acquisition
              intelligence that ties spend to revenue.
            </p>
            <div className="mt-5 flex flex-wrap gap-2">
              {partnershipTags.map((tag) => (
                <span
                  key={tag}
                  className="text-xs font-medium px-3 py-1 rounded-full bg-white text-[#3B6D11] border border-[#97C459]"
                >
                  {tag}
                </span>
              ))}
            </div>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Link
                to="/odoo/one-pager"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-[#3B6D11] text-white font-semibold px-6 py-3 text-sm hover:bg-[#27500A] transition-colors"
              >
                <FileText className="h-4 w-4" />
                Partnership summary
              </Link>
              <Link
                to="/odoo/slide-deck"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-white text-[#3B6D11] font-semibold px-6 py-3 text-sm border border-[#97C459] hover:bg-[#F4FAE9] transition-colors"
              >
                <Presentation className="h-4 w-4" />
                View slide deck
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default OdooPartnershipHome;
